/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  capitalize,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@material-ui/core";
import { Button } from "components/Forms/StyledComponents";
import PageLoading from "components/PageLoading";
import SelectField from "components/Forms/SelectField";
import PillButton from "components/Forms/PillButton";
import {
  OptionGroup,
  OptionTitle,
  StyledPaper,
  VerticalLine,
} from "components/Styled";
import useFormState from "utils/useFormState";
import useTemplateDefinitions from "utils/useTemplateDefinitions";
import apiRequest from "utils/apiRequestWithErrorCode";
import useVariables from "utils/useVariables";
import { allowedStorages } from "utils/canUseTemplates";
import Predicate from "./Predicates";
import DefaultConditions from "./DefaultConditions";
import { FormTouched, hasErrors, validate } from "./validation";
import { StyledTitle } from "../Styled";
import { toFormState, toTemplatePolicy } from "../policyMapper";

import { TemplatePolicy, TemplatePolicyReqBody } from "@dashboard-v3/api";
import { DefaultType, FormState } from "../types";

interface FormProps {
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function Form({ loading, setLoading }: FormProps) {
  const { t } = useTranslation("templatePolicies");
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { state, pathname } = useLocation();
  const { id: paramId } = useParams();
  const { userVariables, loading: varsLoading } = useVariables();
  const isEdit: boolean = pathname.includes("/edit");
  const {
    fetchDefinitions,
    getByStorageType,
    loading: defsLoader,
  } = useTemplateDefinitions();
  const { formState, updateValues, setValues } = useFormState<FormState>(
    initialValues()
  );
  const [touched, setTouched] = useState<FormTouched>({});
  const errors = validate(formState, t);

  useEffect(() => {
    fetchDefinitions();
  }, []);

  useEffect(() => {
    const fetchPolicyById = async (policyId: string) => {
      setLoading(true);
      try {
        const policy = await apiRequest<TemplatePolicy>(
          "GET",
          `/template-policies/${policyId}`
        );
        setValues(toFormState(policy));
        setLoading(false);
      } catch (e: unknown) {
        setLoading(false);
        enqueueSnackbar(t("common:errors.fetchError"), {
          variant: "error",
        });
        history.push("/template-policies");
      }
    };

    const handleEditData = async () => {
      if (state) {
        const { selectedPolicy, policyId } = state;
        return policyId !== paramId
          ? await fetchPolicyById(paramId)
          : setValues(selectedPolicy);
      }
      await fetchPolicyById(paramId);
    };

    if (isEdit) handleEditData();
  }, []);

  const addPredicate = () => {
    setValues({
      predicates: [
        ...formState.predicates,
        {
          definitionId: "",
          variableIdList: [],
          match: true,
          firstMatch: false,
        },
      ],
    });

    setTouched(prev => ({
      ...prev,
      predicates: [...(prev.predicates || []), {}],
    }));
  };

  const removePredicate = (index: number) => {
    if (formState.predicates.length <= 1) return;

    setValues({
      predicates: formState.predicates.filter((_, i) => i !== index),
    });

    setTouched(prev => ({
      ...prev,
      predicates: (prev.predicates || []).filter((_, i) => i !== index),
    }));
  };

  const handleBlur: React.FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = e => {
    const fieldName = e.target.name;
    if (!fieldName) return;
    setTouched(prev => ({ ...prev, [fieldName]: true }));
  };

  const handlePredicateBlur =
    (index: number) => (field: "variableIdList" | "definitionId") => {
      setTouched(prev => {
        const next = [...(prev.predicates || [])];
        next[index] = { ...next[index], [field]: true };
        return { ...prev, predicates: next };
      });
    };

  const handleDefaultBlur = (type: DefaultType) => {
    setTouched(prev => ({
      ...prev,
      defaultDefinitions: { ...prev.defaultDefinitions, [type]: true },
    }));
  };

  const handleDefaultToggle = (enabled: boolean) => {
    if (!enabled) {
      setTouched(prev => ({ ...prev, defaultDefinitions: {} }));
    }
  };

  const markAllTouched = () => {
    const defaultDefinitionsTouched: Record<string, boolean> = {};
    for (const key of Object.keys(formState.defaultDefinitions || {})) {
      defaultDefinitionsTouched[key] = true;
    }

    setTouched({
      name: true,
      description: true,
      cloudStorage: true,
      defaultDefinitions: defaultDefinitionsTouched,
      predicates: formState.predicates.map(() => ({
        variableIdList: true,
        definitionId: true,
      })),
    });
  };

  const savePolicy = async (body: TemplatePolicyReqBody, id?: string) => {
    setLoading(true);
    try {
      const method: "POST" | "PUT" = id ? "PUT" : "POST";
      const path: string = id
        ? `/template-policies/${id}`
        : "/template-policies";
      const res = await apiRequest<TemplatePolicyReqBody>(method, path, body);
      enqueueSnackbar(t("saveSuccess", { name: res.name }), {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(t("errors.saveError"), {
        variant: "error",
      });
    } finally {
      setLoading(false);
      history.push("/template-policies");
    }
  };

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (hasErrors(errors)) {
      markAllTouched();
      return;
    }

    const templatePolicy = toTemplatePolicy(formState);

    if (state && isEdit) {
      const { policyId } = state;
      savePolicy(templatePolicy, policyId);
    } else {
      savePolicy(templatePolicy);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PageLoading loading={loading || varsLoading || defsLoader} />
      <Grid container spacing={5}>
        <Grid item lg={8} xs={12}>
          <StyledPaper>
            <Typography gutterBottom variant="h6">
              {t("form.title")}
            </Typography>
            <OptionGroup>
              <TextField
                name="name"
                label="Template policy name"
                value={formState.name}
                onChange={updateValues}
                onBlur={handleBlur}
                error={Boolean(touched.name && errors.name)}
                helperText={(touched.name && errors.name) || ""}
                variant="outlined"
                fullWidth
                inputProps={{ "data-testid": "tp-name" }}
              />
            </OptionGroup>
            <OptionGroup>
              <TextField
                name="description"
                label={t("form.description.label")}
                value={formState.description}
                onChange={updateValues}
                onBlur={handleBlur}
                error={Boolean(touched.description && errors.description)}
                helperText={(touched.description && errors.description) || ""}
                variant="outlined"
                fullWidth
                inputProps={{ "data-testid": "tp-description" }}
              />
            </OptionGroup>
            <OptionGroup style={{ marginBottom: 35 }}>
              <StyledTitle>{t("form.cloudStorage.title")}</StyledTitle>
              <SelectField
                name="cloudStorage"
                label={t("form.cloudStorage.label")}
                value={formState.cloudStorage}
                onChange={updateValues}
                onBlur={handleBlur}
                error={Boolean(touched.cloudStorage && errors.cloudStorage)}
                helperText={(touched.cloudStorage && errors.cloudStorage) || ""}
                options={[...allowedStorages]}
                data-testid="tp-cloud-storage"
                renderOption={(val, i) => (
                  <MenuItem key={i} value={val}>
                    {capitalize(val.toLowerCase())}
                  </MenuItem>
                )}
              />
            </OptionGroup>
            <OptionGroup>
              <FormLabel>
                <OptionTitle>{t("form.match.title")}</OptionTitle>
              </FormLabel>
              <RadioGroup
                name="match"
                onChange={updateValues}
                value={formState.match}
                data-testid="tp-match"
              >
                <FormControlLabel
                  control={<Radio color="primary" size="small" />}
                  label={t("form.match.first")}
                  value="FIRST"
                />
                <FormControlLabel
                  control={<Radio color="primary" size="small" />}
                  label={t("form.match.all")}
                  value="ALL"
                />
              </RadioGroup>
            </OptionGroup>
          </StyledPaper>
          {formState.predicates.map((_, i) => (
            <Predicate
              key={i}
              predicateIndex={i}
              formState={formState}
              setValues={setValues}
              variables={userVariables}
              definitions={getByStorageType(formState.cloudStorage)}
              errors={errors.predicates?.[i]}
              touched={touched.predicates?.[i]}
              onBlur={handlePredicateBlur(i)}
              onRemove={removePredicate}
            />
          ))}
          <PillButton
            wording={t("form.btn.predicates")}
            onClick={addPredicate}
            lineSeparator="full"
            data-testid="tp-add-condition"
          />
          <DefaultConditions
            formData={{ formState, setValues }}
            definitions={getByStorageType(formState.cloudStorage)}
            errors={errors.defaultDefinitions}
            touched={touched.defaultDefinitions}
            onBlur={handleDefaultBlur}
            onDefaultToggle={handleDefaultToggle}
          />
          <VerticalLine size="sm" />
          <StyledPaper style={{ padding: "25px 24px" }}>
            <Button
              type="submit"
              size="large"
              fullWidth={true}
              wording={t("form.btn.submit")}
              data-testid="tp-save"
            />
          </StyledPaper>
        </Grid>
      </Grid>
    </form>
  );
}

function initialValues(): FormState {
  return {
    name: "",
    description: "",
    match: "FIRST",
    cloudStorage: "",
    predicates: [
      {
        definitionId: "",
        variableIdList: [],
        match: true,
        firstMatch: false,
      },
    ],
    defaultDefinitions: {},
  };
}
